import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Play, ChevronRight, Clock, ChevronLeft, X, Info, Wifi, WifiOff } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { api } from '@/api';
import { resolveMediaUrl, formatDate, formatTime, getYouTubeEmbedUrl } from '@/utils/helpers';
import { GameCard } from '@/components/common/GameCard';
import { VideoTheater } from '@/components/common/VideoTheater';
import { Logo } from '@/components/common/Logo';
import { useScoreWebSocket } from '@/hooks/useScoreWebSocket';
import { SEO } from '@/components/common/SEO';

// Nearby Matches Section Component
const NearbyMatchesSection = () => {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const sliderRef = useRef(null);

  useEffect(() => {
    const loadMatches = async () => {
      try {
        const res = await api.getNearbyMatches();
        setMatches(res.data);
      } catch (error) {
        console.error('Error loading nearby matches:', error);
      } finally {
        setLoading(false);
      }
    };
    loadMatches();
  }, []);

  const scroll = (direction) => {
    if (sliderRef.current) {
      const scrollAmount = 320;
      sliderRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const getStatusBadge = (match) => {
    const status = match.status?.toLowerCase();
    if (status === 'live') {
      return <Badge className="bg-red-500 text-white animate-pulse">LIVE</Badge>;
    } else if (status === 'halftime') {
      return <Badge className="bg-yellow-500 text-black">HT</Badge>;
    } else if (status === 'finished') {
      return <Badge className="bg-gray-600 text-white">FT</Badge>;
    }
    return <Badge className="bg-sky-500 text-white">Upcoming</Badge>;
  };

  const formatMatchTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = date - now;
    const diffHrs = Math.round(diffMs / (1000 * 60 * 60));
    
    if (diffHrs > 0 && diffHrs <= 72) {
      if (diffHrs < 24) return `In ${diffHrs}h`;
      return `In ${Math.round(diffHrs / 24)}d`;
    } else if (diffHrs < 0 && diffHrs >= -72) {
      const absHrs = Math.abs(diffHrs);
      if (absHrs < 24) return `${absHrs}h ago`;
      return `${Math.round(absHrs / 24)}d ago`;
    }
    return formatTime(dateStr);
  };

  if (loading) {
    return (
      <section className="py-12 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <div className="animate-pulse">
            <div className="h-12 bg-gray-700 rounded w-96 mx-auto mb-8" />
            <div className="flex gap-4 overflow-hidden">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="w-72 h-40 bg-gray-800 rounded-xl flex-shrink-0" />
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (matches.length === 0) {
    return null;
  }

  return (
    <section className="py-12 px-4 bg-gradient-to-b from-transparent via-gray-900/50 to-transparent">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <h2 
          className="text-4xl md:text-5xl font-extrabold text-center text-white mb-2"
          style={{ fontFamily: 'Montserrat, sans-serif' }}
        >
          Local Passion. <span className="text-sky-400">Global Reach.</span>
        </h2>
        <p className="text-gray-400 text-center mb-8">Live and upcoming matches within 72 hours</p>

        {/* Slider Container */}
        <div className="relative">
          {/* Left Arrow */}
          {matches.length > 4 && (
            <button
              onClick={() => scroll('left')}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-10 h-10 rounded-full bg-gray-800 hover:bg-gray-700 flex items-center justify-center text-white shadow-lg"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
          )}

          {/* Matches Slider */}
          <div 
            ref={sliderRef}
            className="flex gap-4 overflow-x-auto scrollbar-hide pb-4 scroll-smooth"
            style={{ scrollSnapType: 'x mandatory' }}
          >
            {matches.map((match) => (
              <div
                key={match.id}
                className="flex-shrink-0 w-72 bg-gray-800/60 rounded-xl p-4 border border-gray-700/50 hover:border-sky-500/50 transition-colors"
                style={{ scrollSnapAlign: 'start' }}
              >
                <div className="flex items-center justify-between mb-3">
                  {getStatusBadge(match)}
                  <span className="text-gray-400 text-xs">{formatMatchTime(match.scheduled_time)}</span>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-white font-medium truncate flex-1">{match.home_team_name || 'TBD'}</span>
                    <span className="text-white font-bold text-lg ml-2">
                      {match.home_score ?? '-'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-300 truncate flex-1">{match.away_team_name || 'TBD'}</span>
                    <span className="text-gray-300 font-bold text-lg ml-2">
                      {match.away_score ?? '-'}
                    </span>
                  </div>
                </div>

                <div className="mt-3 pt-3 border-t border-gray-700/50">
                  <p className="text-xs text-sky-300 truncate">
                    {match.sport_name} • {match.league_name}
                  </p>
                  {match.venue && (
                    <p className="text-xs text-gray-500 truncate mt-1">{match.venue}</p>
                  )}
                </div>

                {match.video_sources?.length > 0 && (
                  <Button 
                    size="sm" 
                    className="w-full mt-3 bg-sky-500 hover:bg-sky-600 text-white"
                  >
                    <Play className="w-3 h-3 mr-1" /> Watch
                  </Button>
                )}
              </div>
            ))}
          </div>

          {/* Right Arrow */}
          {matches.length > 4 && (
            <button
              onClick={() => scroll('right')}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-10 h-10 rounded-full bg-gray-800 hover:bg-gray-700 flex items-center justify-center text-white shadow-lg"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

// Live & Upcoming Matches Section
const LiveUpcomingMatchesSection = ({ onWatch }) => {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [liveScores, setLiveScores] = useState({});
  const [scrollPosition, setScrollPosition] = useState(0);
  const scrollContainerRef = React.useRef(null);

  useEffect(() => {
    const loadMatches = async () => {
      try {
        // Get live games and all scheduled games
        const [liveRes, allGamesRes] = await Promise.all([
          api.getLiveGames(),
          api.getGames({ limit: 20 })
        ]);
        
        // Get live games
        const liveGames = liveRes.data.map(g => ({ ...g, isLive: true }));
        
        // Get upcoming/scheduled games sorted by time
        const scheduledGames = allGamesRes.data
          .filter(g => g.status === 'scheduled')
          .sort((a, b) => new Date(a.scheduled_time) - new Date(b.scheduled_time))
          .slice(0, 6)
          .map(g => ({ ...g, isLive: false }));
        
        // Combine: live first, then upcoming
        const combined = [...liveGames, ...scheduledGames].slice(0, 8);
        
        setMatches(combined);
      } catch (error) {
        console.error('Error loading live/upcoming matches:', error);
      } finally {
        setLoading(false);
      }
    };
    loadMatches();
  }, []);

  // Fetch live scores for matches with feed URLs
  useEffect(() => {
    const fetchLiveScores = async () => {
      const matchesWithFeed = matches.filter(m => m.score_feed_url);
      
      for (const match of matchesWithFeed) {
        try {
          const res = await api.getGameLiveScore(match.id);
          if (res.data.text) {
            setLiveScores(prev => ({ ...prev, [match.id]: res.data.text }));
          }
        } catch (error) {
          console.error(`Error fetching live score for ${match.id}:`, error);
        }
      }
    };

    if (matches.length > 0) {
      fetchLiveScores();
      // Refresh every 30 seconds
      const interval = setInterval(fetchLiveScores, 30000);
      return () => clearInterval(interval);
    }
  }, [matches]);

  // Auto-scroll carousel effect
  useEffect(() => {
    if (matches.length <= 3 || !scrollContainerRef.current) return;
    
    const container = scrollContainerRef.current;
    const cardWidth = 304; // 288px card + 16px gap
    const maxScroll = container.scrollWidth - container.clientWidth;
    
    const interval = setInterval(() => {
      setScrollPosition(prev => {
        const newPos = prev + cardWidth;
        if (newPos >= maxScroll) {
          return 0; // Reset to start
        }
        return newPos;
      });
    }, 4000); // Rotate every 4 seconds
    
    return () => clearInterval(interval);
  }, [matches]);

  // Apply scroll position
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      });
    }
  }, [scrollPosition]);

  const formatMatchTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = date - now;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffMs < 0) return 'Started';
    if (diffHours < 1) return 'Starting soon';
    if (diffHours < 24) return `In ${diffHours}h`;
    if (diffDays === 1) return 'Tomorrow';
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <section className="py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="h-6 bg-gray-700 rounded w-56 mb-4 animate-pulse" />
          <div className="flex gap-4 overflow-x-auto pb-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="flex-shrink-0 w-72 h-32 bg-gray-800 rounded-xl animate-pulse" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (matches.length === 0) {
    return null;
  }

  return (
    <section className="py-8 px-4" data-testid="live-upcoming-section">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
            Live & Upcoming Matches
          </h2>
          <Link to="/fixtures" className="text-sky-400 hover:text-sky-300 text-sm flex items-center gap-1">
            View All <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
        
        <div 
          ref={scrollContainerRef}
          className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide"
          onMouseEnter={() => setScrollPosition(scrollContainerRef.current?.scrollLeft || 0)}
        >
          {matches.map(match => {
            // Generate short team names (last word, first 3 chars)
            const homeShort = match.home_team_name?.split(' ').slice(-1)[0]?.substring(0, 3).toUpperCase() || 'HOM';
            const awayShort = match.away_team_name?.split(' ').slice(-1)[0]?.substring(0, 3).toUpperCase() || 'AWY';
            
            return (
              <div
                key={match.id}
                onClick={() => onWatch(match)}
                className="flex-shrink-0 w-72 bg-gray-800/60 rounded-xl p-4 cursor-pointer hover:bg-gray-800/80 transition-colors border border-gray-700/50 hover:border-sky-500/50"
              >
                <div className="flex items-center justify-between mb-3">
                  {match.isLive || match.status === 'live' ? (
                    <Badge className="bg-red-500 text-white text-xs flex items-center gap-1">
                      <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></span>
                      LIVE
                    </Badge>
                  ) : (
                    <Badge className="bg-amber-500/20 text-amber-400 border border-amber-500/30 text-xs">
                      {formatMatchTime(match.scheduled_time)}
                    </Badge>
                  )}
                  <span className="text-gray-400 text-xs truncate ml-2">{match.league_name}</span>
                </div>
                
                {/* Vertical Team Layout */}
                <div className="flex flex-col items-center gap-2">
                  {/* Home Team */}
                  <div className="flex items-center gap-2 w-full justify-center">
                    {match.home_team_logo ? (
                      <img src={resolveMediaUrl(match.home_team_logo)} alt="" className="w-8 h-8 object-contain" />
                    ) : (
                      <div className="w-8 h-8 bg-gray-700 rounded flex items-center justify-center text-xs font-bold text-white">
                        {match.home_team_name?.charAt(0)}
                      </div>
                    )}
                    <span className="text-white font-medium text-sm">{match.home_team_name}</span>
                  </div>
                  
                  {/* Score or VS */}
                  <div className="text-center">
                    {(match.isLive || match.status === 'live' || match.status === 'finished') && match.home_score !== null ? (
                      <div className="text-white font-bold text-lg">
                        {match.home_score} - {match.away_score}
                      </div>
                    ) : (
                      <span className="text-gray-500 text-sm">vs.</span>
                    )}
                  </div>
                  
                  {/* Away Team */}
                  <div className="flex items-center gap-2 w-full justify-center">
                    {match.away_team_logo ? (
                      <img src={resolveMediaUrl(match.away_team_logo)} alt="" className="w-8 h-8 object-contain" />
                    ) : (
                      <div className="w-8 h-8 bg-gray-700 rounded flex items-center justify-center text-xs font-bold text-white">
                        {match.away_team_name?.charAt(0)}
                      </div>
                    )}
                    <span className="text-white font-medium text-sm">{match.away_team_name}</span>
                  </div>
                </div>
                
                {/* Live Score Feed Text with Team Abbreviations */}
                {liveScores[match.id] && (
                  <div className="mt-3 pt-3 border-t border-gray-700">
                    <div className="flex items-center justify-center gap-2">
                      <span className="text-white font-bold text-xs bg-gray-700 px-1.5 py-0.5 rounded">{homeShort}</span>
                      <span className="text-sky-400 text-sm font-medium">{liveScores[match.id]}</span>
                      <span className="text-white font-bold text-xs bg-gray-700 px-1.5 py-0.5 rounded">{awayShort}</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export const HomePage = ({ sports }) => {
  const [featuredGames, setFeaturedGames] = useState([]);
  const [liveGames, setLiveGames] = useState([]);
  const [upcomingGames, setUpcomingGames] = useState([]);
  const [replayGames, setReplayGames] = useState([]);
  const [selectedGame, setSelectedGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [liveScoreTexts, setLiveScoreTexts] = useState({});
  const [showInfoOverlay, setShowInfoOverlay] = useState(false);

  // Handle real-time score updates via WebSocket
  const handleScoreUpdate = useCallback((updatedGame) => {
    // Update featured games
    setFeaturedGames(prev => prev.map(game => 
      game.id === updatedGame.id ? { ...game, ...updatedGame } : game
    ));
    // Update live games
    setLiveGames(prev => prev.map(game => 
      game.id === updatedGame.id ? { ...game, ...updatedGame } : game
    ));
    // Update upcoming games
    setUpcomingGames(prev => prev.map(game => 
      game.id === updatedGame.id ? { ...game, ...updatedGame } : game
    ));
    // Update replay games
    setReplayGames(prev => prev.map(game => 
      game.id === updatedGame.id ? { ...game, ...updatedGame } : game
    ));
  }, []);

  // Connect to WebSocket for real-time score updates
  const { isConnected: wsConnected } = useScoreWebSocket(handleScoreUpdate);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [featured, live, upcoming, replays] = await Promise.all([
          api.getFeaturedGames(),
          api.getLiveGames(),
          api.getUpcomingGames(12),
          api.getReplayGames(12)
        ]);
        setFeaturedGames(featured.data);
        setLiveGames(live.data);
        setUpcomingGames(upcoming.data);
        setReplayGames(replays.data);
      } catch (error) {
        console.error('Error loading home data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Fetch live score text for games with score_feed_url
  useEffect(() => {
    const fetchLiveScores = async () => {
      const allGames = [...featuredGames, ...liveGames];
      const gamesWithFeed = allGames.filter(g => g.score_feed_url);
      
      for (const game of gamesWithFeed) {
        try {
          const res = await api.getGameLiveScore(game.id);
          if (res.data.text) {
            setLiveScoreTexts(prev => ({ ...prev, [game.id]: res.data.text }));
          }
        } catch (error) {
          console.error('Error fetching live score:', error);
        }
      }
    };

    if (featuredGames.length > 0 || liveGames.length > 0) {
      fetchLiveScores();
      const interval = setInterval(fetchLiveScores, 30000);
      return () => clearInterval(interval);
    }
  }, [featuredGames, liveGames]);

  // Auto-rotate hero carousel
  useEffect(() => {
    if (featuredGames.length > 1) {
      const interval = setInterval(() => {
        setCurrentSlide(prev => (prev + 1) % featuredGames.length);
      }, 6000);
      return () => clearInterval(interval);
    }
  }, [featuredGames.length]);

  const currentFeatured = featuredGames[currentSlide];

  const handleWatchNow = (game) => {
    if (!game) return;
    // New stream_provider model — preferred. External-provider games (Plus/Uscreen)
    // open in a new tab; embedded providers (YouTube/Facebook/Twitch/etc) play
    // in the in-page VideoTheater.
    if (game.stream_provider && game.stream_url) {
      const externalProviders = new Set(['uscreen', 'plus', 'nbc_sports']);
      if (externalProviders.has(String(game.stream_provider).toLowerCase())) {
        window.open(game.stream_url, '_blank', 'noopener,noreferrer');
      } else {
        setSelectedGame(game);
      }
      return;
    }
    // Legacy fallback: video_sources[0]
    const primarySource = game.video_sources?.[0];
    if (primarySource?.type === 'uscreen' && !primarySource?.is_free) {
      window.open(primarySource.url, '_blank', 'noopener,noreferrer');
    } else if (primarySource) {
      setSelectedGame(game);
    }
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      <SEO 
        title="Home"
        description="Watch live soccer, GAA, hurling, and rugby matches. Get live scores, schedules, and stream your favorite teams in the United States."
        url="/"
      />
      {/* Hero Carousel - Peacock Style */}
      <section className="relative h-[75vh] min-h-[550px] overflow-hidden">
        {featuredGames.length > 0 ? (
          <>
            {featuredGames.map((game, index) => (
              <div
                key={game.id}
                className={`absolute inset-0 transition-opacity duration-1000 ${index === currentSlide ? 'opacity-100' : 'opacity-0'}`}
              >
                {/* Background image — prefer hero image, fall back to match thumbnail, then gradient */}
                {(game.background_image_url || game.thumbnail) ? (
                  <img
                    src={resolveMediaUrl(game.background_image_url || game.thumbnail)}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{
                      transform: `scale(${(game.hero_zoom || 100) / 100})`,
                      transformOrigin: `${game.hero_position_x || 50}% ${game.hero_position_y || 50}%`,
                      objectPosition: `${game.hero_position_x || 50}% ${game.hero_position_y || 50}%`
                    }}
                  />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-[#0a1628] via-[#1a2d4a] to-[#0d1a2d]" />
                )}
                {/* Gradient overlays for readability */}
                <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/60 to-black/30" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#001428] via-transparent to-transparent" />
                {/* Decorative diagonal line like Peacock */}
                <div className="absolute inset-0 overflow-hidden">
                  <div className="absolute top-0 bottom-0 left-[45%] w-[2px] bg-gradient-to-b from-transparent via-amber-500/40 to-transparent transform -skew-x-12" />
                </div>
              </div>
            ))}

            <div className="relative z-10 h-full flex items-center">
              <div className="max-w-7xl mx-auto px-6 w-full">
                <div className="max-w-xl">
                  {/* Team Logos Row */}
                  <div className="flex items-center gap-4 mb-6">
                    {/* Home Team Logo */}
                    <div className={`w-16 h-16 md:w-20 md:h-20 rounded-lg flex items-center justify-center p-2 ${
                      currentFeatured?.home_team_logo ? '' : 'bg-white/10 backdrop-blur-sm'
                    }`}>
                      {currentFeatured?.home_team_logo ? (
                        <img src={resolveMediaUrl(currentFeatured.home_team_logo)} alt="" className="w-full h-full object-contain" />
                      ) : (
                        <span className="text-2xl md:text-3xl font-bold text-white">{currentFeatured?.home_team_name?.charAt(0) || 'H'}</span>
                      )}
                    </div>
                    {/* League Logo */}
                    <div className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center ${
                      currentFeatured?.league_logo ? 'p-1' : 'bg-white/10 backdrop-blur-sm'
                    }`}>
                      {currentFeatured?.league_logo ? (
                        <img src={resolveMediaUrl(currentFeatured.league_logo)} alt="" className="w-full h-full object-contain" />
                      ) : (
                        <span className="text-xs md:text-sm font-bold text-sky-300">{currentFeatured?.league_name?.split(' ').map(w => w[0]).join('') || 'VS'}</span>
                      )}
                    </div>
                    {/* Away Team Logo */}
                    <div className={`w-16 h-16 md:w-20 md:h-20 rounded-lg flex items-center justify-center p-2 ${
                      currentFeatured?.away_team_logo ? '' : 'bg-white/10 backdrop-blur-sm'
                    }`}>
                      {currentFeatured?.away_team_logo ? (
                        <img src={resolveMediaUrl(currentFeatured.away_team_logo)} alt="" className="w-full h-full object-contain" />
                      ) : (
                        <span className="text-2xl md:text-3xl font-bold text-white">{currentFeatured?.away_team_name?.charAt(0) || 'A'}</span>
                      )}
                    </div>
                  </div>

                  {/* Status Badge & Meta */}
                  <div className="flex items-center gap-3 mb-4">
                    {currentFeatured?.status === 'live' ? (
                      <span className="inline-flex items-center gap-2 bg-red-500 text-white px-3 py-1 rounded text-sm font-semibold">
                        <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                        Live
                      </span>
                    ) : currentFeatured?.status === 'finished' ? (
                      <span className="inline-flex items-center gap-2 bg-gray-700 text-white px-3 py-1 rounded text-sm font-medium border border-gray-600">
                        Replay
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-2 bg-amber-500/20 text-amber-400 px-3 py-1 rounded text-sm font-medium border border-amber-500/30">
                        Upcoming
                      </span>
                    )}
                    <span className="text-gray-400 text-sm">
                      {formatDate(currentFeatured?.scheduled_time)} • {currentFeatured?.sport_name}
                    </span>
                  </div>

                  {/* Match Title - Vertical on mobile, horizontal on desktop */}
                  <div className="mb-3">
                    {/* Mobile: Vertical layout */}
                    <div className="md:hidden">
                      <h1 className="text-3xl font-bold text-white leading-tight">
                        {currentFeatured?.home_team_name || 'TBD'}
                      </h1>
                      <span className="text-gray-500 text-2xl font-bold block my-1">vs.</span>
                      <h1 className="text-3xl font-bold text-white leading-tight">
                        {currentFeatured?.away_team_name || 'TBD'}
                      </h1>
                    </div>
                    {/* Desktop: Horizontal layout */}
                    <h1 className="hidden md:block text-5xl font-bold text-white leading-tight">
                      {currentFeatured?.home_team_name || 'TBD'} <span className="text-gray-500">vs</span> {currentFeatured?.away_team_name || 'TBD'}
                    </h1>
                  </div>

                  {/* Score with team abbreviations (if live or finished) */}
                  {(currentFeatured?.status === 'live' || currentFeatured?.status === 'finished') && 
                   currentFeatured?.home_score !== null && (
                    <div className="flex items-center gap-3 mb-4">
                      <span className="text-white text-lg font-semibold hidden md:inline">
                        {currentFeatured?.home_team_name?.split(' ').slice(-1)[0]?.substring(0, 3).toUpperCase() || 'HOM'}
                      </span>
                      <span className="text-4xl md:text-5xl font-bold text-sky-400">
                        {currentFeatured.home_score} - {currentFeatured.away_score}
                      </span>
                      <span className="text-white text-lg font-semibold hidden md:inline">
                        {currentFeatured?.away_team_name?.split(' ').slice(-1)[0]?.substring(0, 3).toUpperCase() || 'AWY'}
                      </span>
                    </div>
                  )}

                  {/* Live Score Feed Text with team abbreviations */}
                  {liveScoreTexts[currentFeatured?.id] && (
                    <div className="flex items-center gap-2 text-sky-300 text-lg mb-4">
                      <span className="text-white font-semibold text-sm bg-white/10 px-2 py-0.5 rounded">
                        {currentFeatured?.home_team_name?.split(' ').slice(-1)[0]?.substring(0, 3).toUpperCase() || 'HOM'}
                      </span>
                      <span>{liveScoreTexts[currentFeatured.id]}</span>
                      <span className="text-white font-semibold text-sm bg-white/10 px-2 py-0.5 rounded">
                        {currentFeatured?.away_team_name?.split(' ').slice(-1)[0]?.substring(0, 3).toUpperCase() || 'AWY'}
                      </span>
                    </div>
                  )}

                  {/* League Info */}
                  <p className="text-gray-400 mb-6">{currentFeatured?.league_name}</p>

                  {/* Action Buttons - WPSL pill style */}
                  <div className="flex items-center gap-4 flex-wrap">
                    <Button 
                      onClick={() => handleWatchNow(currentFeatured)}
                      className="bg-wpsl-red hover:bg-wpsl-blue text-white text-base px-7 py-5 rounded-full font-bold tracking-wide transition-colors"
                      style={{ letterSpacing: '0.5px' }}
                      data-testid="hero-watch-btn"
                    >
                      <Play className="w-5 h-5 mr-2 fill-current" />
                      {currentFeatured?.status === 'finished' ? 'WATCH REPLAY' : 'WATCH NOW'}
                    </Button>
                    <Button 
                      className="bg-wpsl-blue hover:bg-wpsl-red text-white text-base px-7 py-5 rounded-full font-bold tracking-wide transition-colors border-none"
                      style={{ letterSpacing: '0.5px' }}
                      onClick={() => setShowInfoOverlay(true)}
                      data-testid="hero-info-btn"
                    >
                      <Info className="w-5 h-5 mr-2" />
                      MORE INFO
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Info Overlay - Cable TV Style */}
            {showInfoOverlay && currentFeatured && (
              <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/70 backdrop-blur-sm transition-all duration-300">
                <div className="relative max-w-2xl w-full mx-4 bg-gradient-to-br from-gray-900/95 via-gray-800/95 to-gray-900/95 rounded-xl border border-gray-700/50 shadow-2xl overflow-hidden">
                  {/* Close Button */}
                  <button
                    onClick={() => setShowInfoOverlay(false)}
                    className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-all z-10"
                    data-testid="info-close-btn"
                  >
                    <X className="w-5 h-5" />
                  </button>

                  {/* Header with gradient accent */}
                  <div className="h-1 bg-gradient-to-r from-wpsl-red via-wpsl-blue to-wpsl-red" />
                  
                  <div className="p-6">
                    {/* Match Info Header */}
                    <div className="flex items-center gap-4 mb-4">
                      {/* Team Logos */}
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center p-1">
                          {currentFeatured.home_team_logo ? (
                            <img src={resolveMediaUrl(currentFeatured.home_team_logo)} alt="" className="w-full h-full object-contain" />
                          ) : (
                            <span className="text-lg font-bold text-white">{currentFeatured.home_team_name?.charAt(0)}</span>
                          )}
                        </div>
                        <span className="text-gray-500 text-sm font-medium">VS</span>
                        <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center p-1">
                          {currentFeatured.away_team_logo ? (
                            <img src={resolveMediaUrl(currentFeatured.away_team_logo)} alt="" className="w-full h-full object-contain" />
                          ) : (
                            <span className="text-lg font-bold text-white">{currentFeatured.away_team_name?.charAt(0)}</span>
                          )}
                        </div>
                      </div>
                      
                      {/* Status Badge */}
                      <div className="ml-auto">
                        {currentFeatured.status === 'live' ? (
                          <span className="inline-flex items-center gap-2 bg-red-500 text-white px-3 py-1 rounded text-sm font-semibold">
                            <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                            LIVE
                          </span>
                        ) : currentFeatured.status === 'finished' ? (
                          <span className="bg-gray-700 text-white px-3 py-1 rounded text-sm font-medium">REPLAY</span>
                        ) : (
                          <span className="bg-amber-500/20 text-amber-400 px-3 py-1 rounded text-sm font-medium border border-amber-500/30">UPCOMING</span>
                        )}
                      </div>
                    </div>

                    {/* Match Title */}
                    <h2 className="text-2xl font-bold text-white mb-2">
                      {currentFeatured.home_team_name} vs {currentFeatured.away_team_name}
                    </h2>
                    
                    {/* Meta Info */}
                    <div className="flex flex-wrap items-center gap-3 text-sm text-gray-400 mb-4">
                      <span>{currentFeatured.league_name}</span>
                      <span className="w-1 h-1 rounded-full bg-gray-600"></span>
                      <span>{currentFeatured.sport_name}</span>
                      <span className="w-1 h-1 rounded-full bg-gray-600"></span>
                      <span>{formatDate(currentFeatured.scheduled_time)}</span>
                    </div>

                    {/* Score (if available) */}
                    {(currentFeatured.status === 'live' || currentFeatured.status === 'finished') && 
                     currentFeatured.home_score !== null && (
                      <div className="flex items-center gap-4 p-3 bg-white/5 rounded-lg mb-4">
                        <span className="text-white font-medium">{currentFeatured.home_team_name?.split(' ').slice(-1)[0]}</span>
                        <span className="text-3xl font-bold text-sky-400">{currentFeatured.home_score} - {currentFeatured.away_score}</span>
                        <span className="text-white font-medium">{currentFeatured.away_team_name?.split(' ').slice(-1)[0]}</span>
                      </div>
                    )}

                    {/* Description / Write-up */}
                    <div className="border-t border-gray-700/50 pt-4">
                      <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-2">About This Match</h3>
                      <p className="text-gray-300 leading-relaxed">
                        {currentFeatured.featured_description || 
                          `Watch ${currentFeatured.home_team_name} take on ${currentFeatured.away_team_name} in this ${currentFeatured.league_name} matchup. ${
                            currentFeatured.status === 'live' ? 'The match is currently live!' : 
                            currentFeatured.status === 'finished' ? 'Catch the full replay of this exciting match.' :
                            `Kickoff scheduled for ${formatDate(currentFeatured.scheduled_time)}.`
                          }`
                        }
                      </p>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3 mt-6">
                      <Button 
                        onClick={() => {
                          setShowInfoOverlay(false);
                          handleWatchNow(currentFeatured);
                        }}
                        className="flex-1 bg-amber-500 hover:bg-amber-400 text-black font-semibold"
                      >
                        <Play className="w-4 h-4 mr-2 fill-current" />
                        {currentFeatured.status === 'finished' ? 'WATCH REPLAY' : 'WATCH NOW'}
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => setShowInfoOverlay(false)}
                        className="border-gray-600 text-white hover:bg-white/10"
                      >
                        Close
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Carousel Dots - Peacock Style */}
            {featuredGames.length > 1 && (
              <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex gap-2 z-20">
                {featuredGames.map((game, index) => (
                  <button
                    key={game.id}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-2.5 h-2.5 rounded-full transition-all ${
                      index === currentSlide 
                        ? 'bg-amber-500 w-6' 
                        : 'bg-white/30 hover:bg-white/50'
                    }`}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            )}

            {featuredGames.length > 1 && (
              <>
                <button
                  onClick={() => setCurrentSlide(prev => (prev - 1 + featuredGames.length) % featuredGames.length)}
                  className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center text-white/80 hover:text-white transition-all"
                >
                  <ChevronRight className="w-5 h-5 rotate-180" />
                </button>
                <button
                  onClick={() => setCurrentSlide(prev => (prev + 1) % featuredGames.length)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center text-white/80 hover:text-white transition-all"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </>
            )}
          </>
        ) : (
          <div className="relative h-full bg-gradient-to-br from-[#001428] via-[#002852] to-[#000814]">
            <div className="absolute inset-0 bg-gradient-to-t from-[#001428] via-transparent to-transparent" />
            <div className="relative z-10 h-full flex items-center justify-center text-center px-4">
              <div className="max-w-4xl">
                <h1 className="text-4xl md:text-6xl font-bold text-white mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Building the future of <span className="text-wpsl-red">women's soccer</span>
                </h1>
                <p className="text-xl text-gray-200 mb-8">
                  This is more than a league. It's a movement for growth, ownership, and long-term impact in women's soccer.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <Link
                    to="/fixtures"
                    className="bg-wpsl-red hover:bg-wpsl-blue text-white px-8 py-3 rounded-full font-bold tracking-wide transition-colors"
                    style={{ letterSpacing: '0.5px' }}
                  >
                    Watch Live
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Live Games Row */}
      {liveGames.length > 0 && (
        <section className="py-8 px-4 bg-gradient-to-b from-[#001428] to-transparent">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white flex items-center">
                <span className="live-dot mr-2"></span> Live Now
              </h2>
              <Link to="/scores" className="text-sky-300 hover:text-sky-200 flex items-center text-sm">
                View All <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
              {liveGames.map(game => (
                <div 
                  key={game.id}
                  onClick={() => handleWatchNow(game)}
                  className="flex-shrink-0 w-72 bg-gray-800/60 rounded-xl p-4 cursor-pointer hover:bg-gray-800/80 transition-colors border border-gray-700/50"
                >
                  <div className="flex items-center justify-between mb-3">
                    <Badge className="bg-red-500 text-white text-xs">LIVE</Badge>
                    <span className="text-gray-400 text-xs">{game.league_name}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-white font-medium truncate">{game.home_team_name}</p>
                      <p className="text-gray-400 truncate">{game.away_team_name}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-white font-bold text-xl">{game.home_score ?? 0}</p>
                      <p className="text-gray-400 font-bold text-xl">{game.away_score ?? 0}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Featured Matches Row */}
      {featuredGames.length > 0 && (
        <section className="py-8 px-4">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-xl font-bold text-white mb-4">Featured Matches</h2>
            <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
              {featuredGames.map(game => (
                <div 
                  key={game.id}
                  onClick={() => handleWatchNow(game)}
                  className="flex-shrink-0 w-80 relative rounded-xl overflow-hidden cursor-pointer group"
                >
                  {game.thumbnail ? (
                    <img src={resolveMediaUrl(game.thumbnail)} alt="" className="w-full h-48 object-cover" />
                  ) : (
                    <div className="w-full h-48 bg-gradient-to-br from-gray-700 to-gray-900" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      {game.status === 'live' && (
                        <Badge className="bg-red-500 text-white text-xs">LIVE</Badge>
                      )}
                      {game.status === 'finished' && (
                        <Badge className="bg-gray-600 text-white text-xs">REPLAY</Badge>
                      )}
                      <span className="text-gray-300 text-xs">{game.league_name}</span>
                    </div>
                    <p className="text-white font-semibold">
                      {game.home_team_name} vs {game.away_team_name}
                    </p>
                    {(game.status === 'live' || game.status === 'finished') && game.home_score !== null && (
                      <p className="text-sky-300 font-bold">{game.home_score} - {game.away_score}</p>
                    )}
                  </div>
                  <div className="absolute inset-0 bg-sky-500/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Play className="w-12 h-12 text-white" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Live & Upcoming Matches */}
      {/* Moved below Replays per user request */}

      {/* Upcoming Livestreams Section */}
      <section className="py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-bold text-white">Upcoming Livestreams</h2>
              <Badge className="bg-sky-500 text-white">Coming Soon</Badge>
            </div>
            {upcomingGames.length > 0 && (
              <Link to="/games/upcoming" className="text-sky-400 hover:text-sky-300 text-sm flex items-center gap-1" data-testid="view-all-upcoming">
                View All <ChevronRight className="w-4 h-4" />
              </Link>
            )}
          </div>
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="game-card animate-pulse">
                  <div className="aspect-video bg-gray-700 rounded-t-xl" />
                  <div className="p-4 space-y-2">
                    <div className="h-4 bg-gray-700 rounded w-1/2" />
                    <div className="h-5 bg-gray-700 rounded w-3/4" />
                    <div className="h-4 bg-gray-700 rounded w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {upcomingGames.slice(0, 12).map(game => (
                <GameCard key={game.id} game={game} onWatch={setSelectedGame} />
              ))}
              {upcomingGames.length === 0 && (
                <div className="col-span-full text-center py-8">
                  <p className="text-gray-400">No upcoming livestreams scheduled.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Replays Section */}
      <section className="py-8 px-4 bg-gray-900/30">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-bold text-white">Replays</h2>
              <Badge className="bg-gray-600 text-white">Watch Again</Badge>
            </div>
            {replayGames.length > 0 && (
              <Link to="/games/replays" className="text-sky-400 hover:text-sky-300 text-sm flex items-center gap-1" data-testid="view-all-replays">
                View All <ChevronRight className="w-4 h-4" />
              </Link>
            )}
          </div>
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="game-card animate-pulse">
                  <div className="aspect-video bg-gray-700 rounded-t-xl" />
                  <div className="p-4 space-y-2">
                    <div className="h-4 bg-gray-700 rounded w-1/2" />
                    <div className="h-5 bg-gray-700 rounded w-3/4" />
                    <div className="h-4 bg-gray-700 rounded w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {replayGames.slice(0, 12).map(game => (
                <GameCard key={game.id} game={game} onWatch={setSelectedGame} />
              ))}
              {replayGames.length === 0 && (
                <div className="col-span-full text-center py-8">
                  <p className="text-gray-400">No replays available yet.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Live & Upcoming Matches - positioned below Replays */}
      <LiveUpcomingMatchesSection onWatch={setSelectedGame} />

      {/* Tagline Section */}
      <section className="py-12 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h3 
            className="text-3xl md:text-4xl font-extrabold text-white mb-3"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Building the future of <span className="text-wpsl-red">women's soccer</span>
          </h3>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            The streaming home of the Women's Premier Soccer League — the largest women's soccer league in the world.
          </p>
        </div>
      </section>

      {/* Local Passion. Global Reach Section with Nearby Matches */}
      <NearbyMatchesSection />

      {/* Social Media Section */}
      <section className="py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-xl font-bold text-white mb-2 text-center">Follow Us on Social Media</h2>
          <p className="text-gray-400 text-center mb-6">Stay updated with the latest matches, highlights & behind-the-scenes</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* Instagram */}
            <a 
              href="https://www.instagram.com/wpsl/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group relative overflow-hidden rounded-xl p-6 text-center hover:scale-[1.02] transition-transform"
              style={{ background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)' }}
              data-testid="social-instagram"
            >
              <svg className="w-10 h-10 text-white mx-auto mb-3" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
              <p className="text-white font-semibold">Instagram</p>
              <p className="text-white/80 text-sm">@wpsl</p>
              <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity" />
            </a>

            {/* Facebook */}
            <a 
              href="https://www.facebook.com/WPSL/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group relative overflow-hidden rounded-xl bg-[#1877f2] p-6 text-center hover:scale-[1.02] transition-transform"
              data-testid="social-facebook"
            >
              <svg className="w-10 h-10 text-white mx-auto mb-3" viewBox="0 0 24 24" fill="currentColor">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              <p className="text-white font-semibold">Facebook</p>
              <p className="text-white/80 text-sm">WPSL</p>
              <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity" />
            </a>

            {/* X / Twitter */}
            <a 
              href="https://x.com/WPSL" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group relative overflow-hidden rounded-xl bg-black p-6 text-center hover:scale-[1.02] transition-transform border border-gray-800"
              data-testid="social-twitter"
            >
              <svg className="w-10 h-10 text-white mx-auto mb-3" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
              <p className="text-white font-semibold">X / Twitter</p>
              <p className="text-gray-400 text-sm">@WPSL</p>
              <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity" />
            </a>
          </div>
        </div>
      </section>

      {/* Latest News Section */}
      {/* News removed in WPSL TV rebrand */}

      {/* Browse by Sport removed — WPSL TV is soccer-only */}

      <VideoTheater game={selectedGame} isOpen={!!selectedGame} onClose={() => setSelectedGame(null)} />
    </div>
  );
};

export default HomePage;
